import * as React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';

const CAMPAIGN_DEFAULTS_ENDPOINT = 'https://b.cardcam.io/api/paddle-discount-defaults';
let campaignDefaultsPromise = null;

const loadCampaignDefaults = () => {
    if (campaignDefaultsPromise) return campaignDefaultsPromise;
    campaignDefaultsPromise = fetch(CAMPAIGN_DEFAULTS_ENDPOINT)
        .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
        })
        .catch(() => null);
    return campaignDefaultsPromise;
};

const substituteTokens = (text, data) => {
    if (!text) return '';
    return text.replace(/\[([a-zA-Z_][a-zA-Z0-9_]*)\]/g, (_, key) => {
        if (data && Object.prototype.hasOwnProperty.call(data, key) && data[key] != null) {
            return String(data[key]);
        }
        return '';
    });
};

const SIZE_MAP = {
    Small:  { py: '0.4rem', fontSize: { xs: '0.75rem', md: '0.85rem' } },
    Medium: { py: '0.7rem', fontSize: { xs: '0.85rem', md: '1rem' } },
    Large:  { py: '1rem',   fontSize: { xs: '1rem',    md: '1.15rem' } }
};

const CampaignBanner = ({ topBanner }) => {
    const hasManualText = Boolean(topBanner && (topBanner.text || topBanner.text2));
    const hasCampaignText = Boolean(topBanner && (topBanner.campaignText || topBanner.campaignText2));

    const [campaignDefaults, setCampaignDefaults] = useState(null);

    useEffect(() => {
        if (!topBanner || hasManualText || !hasCampaignText) return;
        let cancelled = false;
        loadCampaignDefaults().then((data) => {
            if (!cancelled) setCampaignDefaults(data);
        });
        return () => { cancelled = true; };
    }, [topBanner, hasManualText, hasCampaignText]);

    const effective = (() => {
        if (!topBanner) return null;
        if (hasManualText) return { text: topBanner.text, text2: topBanner.text2 };
        if (!hasCampaignText) return null;
        if (!campaignDefaults || campaignDefaults.show_need_a_discount_on_signup !== true) return null;
        const text = substituteTokens(topBanner.campaignText || '', campaignDefaults);
        const text2 = substituteTokens(topBanner.campaignText2 || '', campaignDefaults);
        if (!text && !text2) return null;
        return { text, text2 };
    })();

    if (!effective) return null;

    const size = SIZE_MAP[topBanner.size] || SIZE_MAP.Small;
    const bannerSx = {
        position: 'fixed',
        top: '64px',
        left: 0,
        width: '100%',
        backgroundColor: topBanner.bgColor,
        color: topBanner.textColor,
        textAlign: 'center',
        py: size.py,
        px: '1rem',
        fontSize: size.fontSize,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        zIndex: 10999,
        textDecoration: 'none',
        display: 'block',
        borderRadius: '0 !important',
        transition: 'filter 0.2s ease',
        cursor: topBanner.link ? 'pointer' : 'default',
        '&:hover': topBanner.link ? { filter: 'brightness(1.15)', textDecoration: 'underline' } : {}
    };
    const arrow = topBanner.link ? ' →' : '';
    const content = (
        <>
            <Box component="span" sx={{ display: 'inline' }}>{effective.text}</Box>
            {effective.text2 && (
                <Box component="span" sx={{ display: { xs: 'block', md: 'inline' }, ml: { md: '1rem' } }}>
                    {effective.text2}{arrow}
                </Box>
            )}
            {!effective.text2 && arrow}
        </>
    );

    const banner = topBanner.link ? (
        <Box component="a" href={topBanner.link} sx={bannerSx}>{content}</Box>
    ) : (
        <Box sx={bannerSx}>{content}</Box>
    );

    if (typeof document === 'undefined') return null;
    return createPortal(banner, document.body);
};

export default CampaignBanner;
